import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate, ROLE_LABELS } from "../lib/format";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Trash2, ShieldCheck } from "lucide-react";

const ROLES = ["admin", "finance_officer", "staff", "board", "auditor"];

export default function UserManagement() {
  const [users, setUsers] = useState(null);
  const [allow, setAllow] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: "email", value: "", role: "staff" });

  const load = () => {
    api.get("/auth/users").then((r) => setUsers(r.data));
    api.get("/admin/allowlist").then((r) => setAllow(r.data));
  };
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role } : x)));
    try { await api.patch(`/admin/users/${u.id}`, { role }); toast.success(`${u.name || u.email} → ${ROLE_LABELS[role]}`); }
    catch (err) { toast.error(apiError(err)); load(); }
  };

  const addEntry = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/allowlist", form);
      toast.success("Access entry added");
      setOpen(false);
      setForm({ type: "email", value: "", role: "staff" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const removeEntry = async (id) => {
    try { await api.delete(`/admin/allowlist/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!users) return <Loader />;

  const userCols = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name || "—"}</span> },
    { key: "email", label: "Email", className: "text-[#5C5C54]" },
    { key: "auth_provider", label: "Sign-in", render: (r) => <StatusBadge tone="gray">{r.auth_provider === "google" ? "Google" : "Password"}</StatusBadge> },
    { key: "role", label: "Role", render: (r) => (
      <select value={r.role} onChange={(e) => setRole(r, e.target.value)} data-testid={`role-${r.id}`}
        className="text-xs border border-stone-300 rounded px-2 py-1 bg-white">
        {ROLES.map((role) => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}
      </select>
    )},
  ];

  return (
    <div>
      <PageHeader title="User Management" subtitle="Manage staff roles and control who can sign in with Google." />

      <h2 className="text-lg font-medium text-[#1A1A1A] mb-3">Users</h2>
      <DataTable columns={userCols} rows={users} testid="users-table" />

      <div className="mt-10 flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-medium text-[#1A1A1A] flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-forest" /> Google Sign-in Access</h2>
          <p className="text-sm text-[#5C5C54]">Only these emails / domains may sign in with Google. Empty list = open to anyone.</p>
        </div>
        <AddButton onClick={() => setOpen(true)} label="Add Access" testid="add-allow-btn" />
      </div>
      <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" data-testid="allowlist">
        {allow.length === 0 && <p className="text-sm text-[#5C5C54] p-4">No restrictions — anyone can sign in with Google.</p>}
        {allow.map((a) => (
          <div key={a.id} className="flex items-center gap-4 px-4 py-3" data-testid={`allow-${a.id}`}>
            <StatusBadge tone={a.type === "domain" ? "blue" : "green"}>{a.type}</StatusBadge>
            <span className="font-mono text-sm text-[#1A1A1A] flex-1">{a.type === "domain" ? `@${a.value}` : a.value}</span>
            <StatusBadge tone="gray">{ROLE_LABELS[a.role] || a.role}</StatusBadge>
            <button onClick={() => removeEntry(a.id)} data-testid={`del-allow-${a.id}`} className="text-stone-400 hover:text-outflow transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <Modal open={open} onOpenChange={setOpen} title="Add Sign-in Access" onSubmit={addEntry} busy={busy} submitLabel="Add" testid="allow-modal">
        <Field label="Type"><SelectInput value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} options={[{ value: "email", label: "Specific email" }, { value: "domain", label: "Email domain" }]} /></Field>
        <Field label="Default Role"><SelectInput value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} /></Field>
        <Field label={form.type === "domain" ? "Domain (e.g. davidsharmonyhouse.org)" : "Email address"} span={2}>
          <TextInput required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
            placeholder={form.type === "domain" ? "davidsharmonyhouse.org" : "person@example.com"} />
        </Field>
      </Modal>
    </div>
  );
}
